/*  FLOWERTRAIL — lotus hero · still-lake ripples (2026-10-18)
    The lotus floats on a dark lake: soft, 3D-shaded ripple rings emanate from its base and flow
    outward across a perspective-flattened water plane, black & white only, subtle. They begin
    almost invisible while the lotus is closed and widen/strengthen as it opens on scroll, so they
    never compete with the flower (the lake canvas sits UNDER the lotus canvas).
    Speed: one band canvas around the water line only, lazy WebGL, renders only while the hero is
    on screen, 30fps on touch / 60 desktop, soft content → low backing store (1× DPR, adaptive
    down to 0.5×), paused on tab-hide / pinch-zoom. Reads lotus progress from window.__ftLotusP. */
(function () {
  'use strict';
  if (window.__ftLotusLake) return;
  window.__ftLotusLake = true;
  var reduce = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia && matchMedia('(pointer: coarse)').matches;

  /* where the flower meets the water inside a lotus frame (measured from the frame alpha) */
  var BASE_X = 0.51, BASE_Y = 0.735, LOTUS_W = 0.62;   /* LOTUS_W: open-flower width as a fraction of the frame */
  var SPAN = 0.62;                                      /* band half-height in lotus widths */

  var VS = 'attribute vec2 aPos;varying vec2 vUv;void main(){vUv=aPos*.5+.5;gl_Position=vec4(aPos,0.,1.);}';
  var FS = [
    'precision mediump float;',
    'varying vec2 vUv;',
    'uniform vec4 uMap;',        /* lake coords: x = vUv.x*uMap.x+uMap.y (lotus widths, 0 = base), y = vUv.y*uMap.z+uMap.w (lotus widths, + = far side) */
    'uniform float uTime,uP,uOn;',
    'float hgt(float r,float t){',                                   /* outward-travelling ripple trains */
    ' return .55*sin(r*17.-t*1.15)*exp(-r*.9)+.30*sin(r*29.-t*1.9+1.7)*exp(-r*1.5)+.18*sin(r*9.5-t*.7+.6)*exp(-r*.6);}',
    'void main(){',
    ' float x=vUv.x*uMap.x+uMap.y;float y=vUv.y*uMap.z+uMap.w;',
    ' float e=mix(.34,.22,smoothstep(-.12,.12,y));',               /* perspective: far side flatter, blended — no seam */
    ' float yy=y/e;float r=length(vec2(x,yy));',
    ' float t=uTime;',
    ' float h=hgt(r,t);float dh=(hgt(r+.004,t)-h)/.004;',          /* slope across the ring */
    ' float lit=-dh*(yy/max(r,1e-3))*.045;',                        /* screen-space slope toward a far light: continuous everywhere */
    ' float grow=.38+2.0*smoothstep(.04,.85,uP);',                  /* rings reach further as the lotus opens */
    ' float reach=1.-smoothstep(grow*.7,grow,r);',
    ' float inner=smoothstep(.16,.32,r);',                          /* nothing under the flower itself */
    ' float strength=(.22+.78*smoothstep(.04,.6,uP))*reach*inner;',
    ' float fade=smoothstep(0.,.12,vUv.x)*smoothstep(1.,.88,vUv.x)*smoothstep(0.,.25,vUv.y)*smoothstep(1.,.75,vUv.y);',
    ' float k=strength*fade*uOn;',
    ' float hi=clamp(lit,0.,1.);float lo=clamp(-lit,0.,1.);',
    ' hi=hi*hi*1.6;',                                                /* tight moonlit crests */
    ' float a=clamp(hi*.85+lo*.55,0.,1.)*k*.55;',                    /* subtle: max ~.4 even on the brightest crest */
    ' vec3 c=vec3(hi*.92)*k*.55;',                                    /* white highlights; dark troughs only darken (alpha) */
    ' gl_FragColor=vec4(c,a);}'
  ].join('\n');

  var frozen = false;
  window.addEventListener('ft:zoom-start', function () { frozen = true; });
  window.addEventListener('ft:zoom-end', function () { frozen = false; });
  var SCALES = coarse ? [1, 0.75, 0.5] : [1, 0.75, 0.5];

  function build(lotus) {
    var host = lotus.parentElement; if (!host) return;
    var hero = lotus.closest('[data-scroll-container]') || host;
    var canvas = document.createElement('canvas');
    canvas.className = 'ft-lotus-lake'; canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:absolute;left:0;display:block;pointer-events:none;z-index:0;contain:strict';
    host.insertBefore(canvas, lotus);                                   /* under the flower */
    var gl = null, U = null, geo = null, scaleIdx = 0;
    var visible = false, running = false, last = 0, frameMs = coarse ? 1000 / 30 : 1000 / 60;
    var t0 = performance.now() - Math.random() * 30000, slow = 0, sampled = 0, on = 0;

    function initGL() {
      if (gl) return true;
      gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, powerPreference: 'low-power' });
      if (!gl) return false;
      function sh(type, src) { var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s)); return s; }
      var prog = gl.createProgram();
      gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS)); gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS)); gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
      gl.useProgram(prog);
      var buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf); gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      var loc = gl.getAttribLocation(prog, 'aPos'); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      U = {}; ['uMap', 'uTime', 'uP', 'uOn'].forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });
      gl.clearColor(0, 0, 0, 0); gl.disable(gl.DITHER);
      canvas.addEventListener('webglcontextlost', function (e) { e.preventDefault(); gl = null; running = false; }, false);
      layout();
      return true;
    }
    function layout() {
      var lr = lotus.getBoundingClientRect(), hr = host.getBoundingClientRect();
      if (!lr.width || !hr.width) return;
      var Lw = lr.width * LOTUS_W;
      var bx = lr.left - hr.left + lr.width * BASE_X, by = lr.top - hr.top + lr.height * BASE_Y;
      var top = by - SPAN * Lw, hgt = 2 * SPAN * Lw, w = hr.width;
      canvas.style.top = top + 'px'; canvas.style.height = hgt + 'px'; canvas.style.width = w + 'px';
      geo = { w: w, h: hgt, bx: bx, Lw: Lw };
      applyScale();
    }
    function applyScale() {
      if (!gl || !geo) return;
      var s = Math.min(SCALES[scaleIdx], window.devicePixelRatio || 1);
      var W = Math.max(64, Math.round(geo.w * s)), H = Math.max(16, Math.round(geo.h * s));
      if (canvas.width !== W || canvas.height !== H) { canvas.width = W; canvas.height = H; }
      gl.viewport(0, 0, W, H);
      /* x in lotus widths from the base; y in lotus widths, +up (far side) */
      gl.uniform4f(U.uMap, geo.w / geo.Lw, -geo.bx / geo.Lw, 2 * SPAN, -SPAN);
    }
    function frame(now) {
      if (!visible || document.hidden || !gl) { running = false; return; }
      var dt = now - last;
      if (!frozen && dt >= frameMs - 1) {
        if (last) { sampled++; if (dt > frameMs * 1.6) slow++; if (sampled >= 45) { if (slow > 18 && scaleIdx < SCALES.length - 1) { scaleIdx++; applyScale(); } sampled = 0; slow = 0; } }
        last = now;
        on += (1 - on) * 0.05;                                         /* fade in on first view */
        gl.uniform1f(U.uTime, (now - t0) / 1000); gl.uniform1f(U.uP, Math.max(0, Math.min(1, window.__ftLotusP || 0))); gl.uniform1f(U.uOn, on);
        gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLES, 0, 3);
      }
      requestAnimationFrame(frame);
    }
    function wake() { if (!running && visible && gl && !document.hidden) { running = true; last = 0; requestAnimationFrame(frame); } }
    var rz = null;
    function onResize() { clearTimeout(rz); rz = setTimeout(layout, 150); }
    if ('ResizeObserver' in window) { var ro = new ResizeObserver(onResize); ro.observe(host); ro.observe(lotus); } else window.addEventListener('resize', onResize, { passive: true });
    if (reduce) {
      try { if (initGL()) { gl.uniform1f(U.uTime, 2); gl.uniform1f(U.uP, 0.6); gl.uniform1f(U.uOn, 1); gl.clear(gl.COLOR_BUFFER_BIT); gl.drawArrays(gl.TRIANGLES, 0, 3); } } catch (e) {}
      return;
    }
    /* start after the first lotus frame has painted, so the lake never delays the hero */
    var go = function () { try { if (initGL()) wake(); } catch (e) { if (window.console) console.warn('ft-lotus-lake', e); } };
    if ('requestIdleCallback' in window) requestIdleCallback(go, { timeout: 1500 }); else setTimeout(go, 600);
    new IntersectionObserver(function (en) { visible = en[0].isIntersecting; wake(); }).observe(hero);
    document.addEventListener('visibilitychange', wake);
  }

  function arm() {
    document.querySelectorAll('canvas[data-scroll-canvas][data-sequence-mode="true"]').forEach(function (c) {
      if (c.__ftLake) return; c.__ftLake = 1;
      try { build(c); } catch (e) { if (window.console) console.warn('ft-lotus-lake', e); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', arm); else arm();
  document.addEventListener('shopify:section:load', arm);
})();
